import { useEffect, useState } from "react";
import { Button } from "../../../components/button/button";
import { Input } from "../../../components/input/input";
import { Retractable } from "../../../components/retractable/retractable";
import { Spring } from "../../../components/spring/spring";
import { useAnimatedNavigate } from "../../../hooks/useAnimatedNavigate";
import styles from "../_styles/authPage.module.css";
import AuthAPI from "../../../api/AuthAPI";
import UserAPI from "../../../api/UserAPI";

import { Text } from "../../../components/text/text";

const OTP_COOLDOWN_SECONDS = 120;

const formatTimer = (seconds) => {
    const safeSeconds = Math.max(0, Number(seconds) || 0);
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
};

export const UpdatePasswordPage = () => {
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [otpCode, setOtpCode] = useState("");
    const [challengeId, setChallengeId] = useState("");
    const [requires2fa, setRequires2fa] = useState(null);

    const [currentPasswordError, setCurrentPasswordError] = useState(false);
    const [newPasswordError, setNewPasswordError] = useState(false);
    const [confirmPasswordError, setConfirmPasswordError] = useState(false);
    const [otpCodeError, setOtpCodeError] = useState(false);

    const [errorText, setErrorText] = useState("");
    const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);

    const [loadingStatus, setLoadingStatus] = useState(true);
    const [otpRequestLoading, setOtpRequestLoading] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => setCurrentPasswordError(false), [currentPassword]);
    useEffect(() => setNewPasswordError(false), [newPassword]);
    useEffect(() => setConfirmPasswordError(false), [confirmPassword]);
    useEffect(() => setOtpCodeError(false), [otpCode]);
    useEffect(() => setErrorText(""), [currentPassword, newPassword, confirmPassword, otpCode]);

    useEffect(() => {
        if (otpSecondsLeft <= 0) {
            return;
        }

        const timerId = setInterval(() => {
            setOtpSecondsLeft((prev) => Math.max(0, prev - 1));
        }, 1000);

        return () => {
            clearInterval(timerId);
        };
    }, [otpSecondsLeft]);

    const navigate = useAnimatedNavigate();

    useEffect(() => {
        const load2faStatus = async () => {
            setLoadingStatus(true);

            try {
                const response = await AuthAPI.get2faStatus();
                setRequires2fa(response.is2faEnabled);
            } catch (error) {
                setRequires2fa(false);
                setErrorText(error?.response?.data?.detail || "Не удалось получить статус 2FA");
            } finally {
                setLoadingStatus(false);
            }
        };

        load2faStatus();
    }, []);

    const validatePasswords = () => {
        let hasError = false;

        if (!currentPassword) {
            setCurrentPasswordError(true);
            hasError = true;
        }

        if (!newPassword) {
            setNewPasswordError(true);
            hasError = true;
        }

        if (newPassword !== confirmPassword) {
            setConfirmPasswordError(true);
            setErrorText("Пароли не совпадают");
            hasError = true;
        }

        return hasError;
    };

    const performPasswordUpdate = async () => {
        await UserAPI.updatePassword(currentPassword, newPassword, {
            challengeId: challengeId || null,
            otpCode: challengeId ? otpCode.trim() : null,
        });

        navigate(-1);
    };

    const requestOtp = async () => {
        setOtpRequestLoading(true);
        setErrorText("");

        try {
            const response = await UserAPI.requestUpdatePasswordOtp(currentPassword);

            if (!response.requiresOtp) {
                await performPasswordUpdate();
                return;
            }

            if (!response.challengeId) {
                setErrorText("Не удалось получить challenge для подтверждения");
                return;
            }

            setChallengeId(response.challengeId);
            setOtpCode("");
            const expiresIn = Number(response.expiresIn) || OTP_COOLDOWN_SECONDS;
            setOtpSecondsLeft(Math.min(expiresIn, OTP_COOLDOWN_SECONDS));
        } catch (error) {
            setErrorText(error?.response?.data?.detail || "Ошибка при запросе кода подтверждения");
        } finally {
            setOtpRequestLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (validatePasswords()) {
            return;
        }

        setErrorText("");

        if (requires2fa && !challengeId) {
            await requestOtp();
            return;
        }

        if (requires2fa && challengeId && otpCode.trim().length < 4) {
            setOtpCodeError(true);
            setErrorText("Введите корректный код подтверждения");
            return;
        }

        if (requires2fa && challengeId && otpSecondsLeft === 0) {
            setErrorText("Срок действия кода истек. Запросите новый код");
            return;
        }

        setLoading(true);
        try {
            await performPasswordUpdate();
        } catch (error) {
            setErrorText(error?.response?.data?.detail || "Ошибка при обновлении пароля");
        } finally {
            setLoading(false);
        }
    };

    const resetOtpFlow = () => {
        setChallengeId("");
        setOtpCode("");
        setOtpCodeError(false);
        setOtpSecondsLeft(0);
        setErrorText("");
    };

    const busy = loading || otpRequestLoading || loadingStatus;
    const isOtpStep = Boolean(challengeId);
    const submitText = isOtpStep
        ? (loading ? "Подтверждаем..." : "Подтвердить и сменить пароль")
        : (requires2fa ? (otpRequestLoading ? "Отправляем код..." : "Получить код подтверждения") : (loading ? "Обновляем пароль..." : "Обновить пароль"));
    const isCodeExpired = isOtpStep && otpSecondsLeft === 0;
    const isResendBlockedByTimer = isOtpStep && otpSecondsLeft > 0;
    const resendText = isResendBlockedByTimer
        ? `Отправить код заново (${formatTimer(otpSecondsLeft)})`
        : (otpRequestLoading ? "Отправляем..." : "Отправить код заново");

    const handleCancel = () => {
        if (isOtpStep) {
            resetOtpFlow();
            return;
        }

        navigate(-1);
    };

    return (
        <>
            <Text as="div" variant="h3" className={styles.title} size={28} weight={700}>Обновление пароля</Text>

            <form onSubmit={handleSubmit}>
                <Retractable active={!isOtpStep}>
                    <div className={styles.form}>
                        <Input
                            value={currentPassword}
                            setValue={setCurrentPassword}
                            label="Текущий пароль"
                            type="password"
                            error={currentPasswordError}
                        />

                        <Input
                            value={newPassword}
                            setValue={setNewPassword}
                            label="Новый пароль"
                            type="password"
                            error={newPasswordError}
                        />

                        <Input
                            value={confirmPassword}
                            setValue={setConfirmPassword}
                            label="Повторите пароль"
                            type="password"
                            error={confirmPasswordError}
                        />
                    </div>
                </Retractable>

                <Retractable active={isOtpStep}>
                    <div className={styles.form}>
                        <Input
                            value={otpCode}
                            setValue={setOtpCode}
                            label="Код подтверждения"
                            placeholder="123456"
                            autoComplete="one-time-code"
                            inputMode="numeric"
                            error={otpCodeError}
                        />

                        {
                            isCodeExpired &&
                            <Text as="div" align="center" variant="caption" className={styles.errorText} size={14}>
                                Срок действия кода истек. Запросите новый код.
                            </Text>
                        }
                    </div>
                </Retractable>

                <Spring />

                <Button
                    style={{ marginTop: "14px" }}
                    buttonType="submit"
                    text={submitText}
                    disabled={busy || isCodeExpired}
                />

                <Retractable active={isOtpStep}>
                    <Button
                        type="secondary"
                        text={resendText}
                        onClick={requestOtp}
                        disabled={busy || isResendBlockedByTimer}
                    />
                </Retractable>
            </form>

            <Button
                type="secondary"
                text="Отмена"
                onClick={handleCancel}
                disabled={busy}
            />


            {
                !!errorText &&
                <Text as="div" align="center" variant="body" className={styles.errorText} size={14} lineHeight="1.3">{errorText}</Text>
            }
        </>
    );
}